import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { 
  ChevronRight, 
  ChevronLeft,
  CheckCircle2,
  Loader2,
  Rocket,
  Clock,
  Sparkles,
  Globe,
  Users,
  MapPin,
  Megaphone,
  Phone,
  Menu,
  X
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Quoteplicity Logo Component
const QuoteplicityLogo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_agent-qualifier-form/artifacts/12vhtlnd_quoteplicity%20%282%29.svg" 
    alt="Quoteplicity" 
    className="h-8 sm:h-10 w-auto"
  />
);

// Navigation Header Component
const NavigationHeader = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Logo */}
          <a href="https://www.quoteplicity.com/" target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
            <QuoteplicityLogo />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <a 
              href="https://www.quoteplicity.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-600 hover:text-[#10c0a5] font-medium transition-colors"
            >
              Home
            </a>
            <a 
              href="https://www.quoteplicity.com/features/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-600 hover:text-[#10c0a5] font-medium transition-colors"
            >
              Features
            </a>
            <a 
              href="https://www.quoteplicity.com/pricing/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-600 hover:text-[#10c0a5] font-medium transition-colors"
            >
              Pricing
            </a>
            <a 
              href="https://www.quoteplicity.com/contact/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-600 hover:text-[#10c0a5] font-medium transition-colors"
            >
              Contact
            </a>
          </nav>

          {/* Phone Number & CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <a 
              href="tel:6029021072" 
              className="flex items-center gap-2 text-slate-700 hover:text-[#10c0a5] font-medium transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span>(602) 902-1072</span>
            </a>
            <a 
              href="https://www.quoteplicity.com/try-for-free/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-[#10c0a5] hover:bg-[#0ea892] text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Try For Free
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-slate-600"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-100">
            <nav className="flex flex-col space-y-3">
              <a 
                href="https://www.quoteplicity.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-[#10c0a5] font-medium py-2"
              >
                Home
              </a>
              <a 
                href="https://www.quoteplicity.com/features/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-[#10c0a5] font-medium py-2"
              >
                Features
              </a>
              <a 
                href="https://www.quoteplicity.com/pricing/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-[#10c0a5] font-medium py-2"
              >
                Pricing
              </a>
              <a 
                href="https://www.quoteplicity.com/contact/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-[#10c0a5] font-medium py-2"
              >
                Contact
              </a>
              <a 
                href="tel:6029021072" 
                className="flex items-center gap-2 text-slate-700 font-medium py-2"
              >
                <Phone className="w-4 h-4" />
                <span>(602) 902-1072</span>
              </a>
              <a 
                href="https://www.quoteplicity.com/try-for-free/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-[#10c0a5] hover:bg-[#0ea892] text-white px-4 py-2 rounded-lg font-medium text-center"
              >
                Try For Free
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-slate-50 border-t border-slate-200 py-8 px-4 mt-12">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col items-center text-center">
          {/* Logo & Description */}
          <a href="https://www.quoteplicity.com/" target="_blank" rel="noopener noreferrer">
            <img 
              src="https://customer-assets.emergentagent.com/job_agent-qualifier-form/artifacts/12vhtlnd_quoteplicity%20%282%29.svg" 
              alt="Quoteplicity" 
              className="h-6 mb-3 hover:opacity-80 transition-opacity"
            />
          </a>
          <p className="text-slate-500 text-sm max-w-md mb-3">
            Quoteplicity provides life insurance quoting software and tools for agents, brokers, and agencies to grow their business.
          </p>
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-slate-400" />
            <a href="tel:6029021072" className="text-slate-500 hover:text-[#10c0a5] transition-colors text-sm">
              (602) 902-1072
            </a>
          </div>
          <p className="text-slate-400 text-xs mt-4">
            © {new Date().getFullYear()} Quoteplicity. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Plan options
const planOptions = [
  { 
    id: 'basic', 
    name: 'Basic', 
    price: '$59/mo',
    description: 'For solo agents who don\'t need a website or funnel—just powerful quoting tools'
  },
  { 
    id: 'starter', 
    name: 'Starter', 
    price: '$97/mo',
    description: 'For independent agents with a single website who want to start capturing leads online'
  },
  { 
    id: 'professional', 
    name: 'Professional', 
    price: '$199/mo',
    description: 'For growth-minded agents or small teams ready for full white-labeling and CRM integrations'
  },
  { 
    id: 'team', 
    name: 'Agency', 
    price: '$399/mo',
    description: 'For agencies managing multiple agents and brands with full resell capabilities'
  },
];

// Agency size options
const agencySizeOptions = [
  { value: '1', label: 'Just me' },
  { value: '2-5', label: '2-5 people' },
  { value: '6-10', label: '6-10 people' },
  { value: '11-25', label: '11-25 people' },
  { value: '26-50', label: '26-50 people' },
  { value: '50+', label: '50+ people' },
];

// States licensed options
const statesLicensedOptions = [
  { value: '1-5', label: '1-5 states' },
  { value: '6-10', label: '6-10 states' },
  { value: '11-25', label: '11-25 states' },
  { value: '26-40', label: '26-40 states' },
  { value: '41-50', label: '41-50 states' },
  { value: 'all', label: 'All 50 states' },
];

// Ad platforms
const adPlatforms = [
  { id: 'facebook', label: 'Facebook' },
  { id: 'google', label: 'Google' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'twitter', label: 'Twitter/X' },
  { id: 'other', label: 'Other' },
];

// Phone number formatter
const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/\D/g, '');
  if (phoneNumber.length < 4) return phoneNumber;
  if (phoneNumber.length < 7) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

// Website URL validator
const isValidUrl = (url) => {
  if (!url || url === '') return true;
  return /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(url);
};

// Validation schemas
const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string()
    .min(10, 'Phone number must be at least 10 digits')
    .regex(/^[\d\s\-\(\)\+]+$/, 'Please enter a valid phone number'),
  company_name: z.string().min(2, 'Company name must be at least 2 characters'),
});

// Animation variants
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

export const WaitlistPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  
  // Form data state
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [hasWebsite, setHasWebsite] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [usesCRM, setUsesCRM] = useState(null);
  const [crmName, setCrmName] = useState('');
  const [agencySize, setAgencySize] = useState('');
  const [statesLicensed, setStatesLicensed] = useState('');
  const [runsPaidAds, setRunsPaidAds] = useState(null);
  const [adPlatformsSelected, setAdPlatformsSelected] = useState([]);
  const [otherPlatform, setOtherPlatform] = useState('');
  const [contactData, setContactData] = useState({});

  const totalSteps = 4;
  const progress = (currentStep / totalSteps) * 100;

  const widgetRef = useRef(null);

  const contactForm = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      company_name: '',
    },
  });

  // Scroll to top and notify parent
  const scrollToTop = () => {
    if (widgetRef.current) {
      widgetRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    window.parent.postMessage({ type: 'scrollToTop' }, '*');
  };

  useEffect(() => {
    const sendHeight = () => {
      if (widgetRef.current) {
        const height = widgetRef.current.scrollHeight;
        window.parent.postMessage({ type: 'resize', height: height + 40 }, '*');
      }
    };

    scrollToTop();
    sendHeight();
    const timeout = setTimeout(sendHeight, 500);
    
    const resizeObserver = new ResizeObserver(sendHeight);
    if (widgetRef.current) {
      resizeObserver.observe(widgetRef.current);
    }

    return () => {
      clearTimeout(timeout);
      resizeObserver.disconnect();
    };
  }, [currentStep]);

  const handlePlatformToggle = (platformId) => {
    setAdPlatformsSelected(prev => 
      prev.includes(platformId) 
        ? prev.filter(id => id !== platformId)
        : [...prev, platformId]
    );
  };

  const handleContactSubmit = async (data) => {
    setContactData(data);
    
    // Submit the form directly
    setIsSubmitting(true);
    
    // Combine ad platforms with custom "other" entry
    let platforms = runsPaidAds ? [...adPlatformsSelected] : [];
    if (adPlatformsSelected.includes('other') && otherPlatform) {
      platforms = platforms.filter(p => p !== 'other');
      platforms.push(`Other: ${otherPlatform}`);
    }
    
    const submissionData = {
      ...data,
      plan_interest: selectedPlan,
      has_website: hasWebsite,
      website_url: hasWebsite ? websiteUrl : null,
      uses_crm: usesCRM,
      crm_name: usesCRM ? crmName : null,
      agency_size: agencySize,
      states_licensed: statesLicensed,
      runs_paid_ads: runsPaidAds,
      ad_platforms: platforms,
    };

    try {
      await axios.post(`${API}/waitlist`, submissionData);
      setSubmissionSuccess(true);
    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinalSubmit = async () => {
    setIsSubmitting(true);
    
    // Combine ad platforms with custom "other" entry
    let platforms = runsPaidAds ? [...adPlatformsSelected] : [];
    if (adPlatformsSelected.includes('other') && otherPlatform) {
      platforms = platforms.filter(p => p !== 'other');
      platforms.push(`Other: ${otherPlatform}`);
    }
    
    const submissionData = {
      ...contactData,
      plan_interest: selectedPlan,
      has_website: hasWebsite,
      website_url: hasWebsite ? websiteUrl : null,
      uses_crm: usesCRM,
      crm_name: usesCRM ? crmName : null,
      agency_size: agencySize,
      states_licensed: statesLicensed,
      runs_paid_ads: runsPaidAds,
      ad_platforms: platforms,
    };

    try {
      await axios.post(`${API}/waitlist`, submissionData);
      setSubmissionSuccess(true);
    } catch (error) {
      console.error('Submission error:', error);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceedStep1 = selectedPlan !== null;
  const canProceedStep2 = hasWebsite !== null && usesCRM !== null && agencySize && statesLicensed;

  return (
    <div ref={widgetRef} className="widget-container" data-testid="waitlist-page">
      {/* Navigation Header */}
      <NavigationHeader />
      
      <div className="py-8 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Clock className="w-4 h-4" />
            Exciting Updates Coming Soon
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Join the Waitlist
          </h1>
          <p className="text-slate-600 text-base md:text-lg max-w-xl mx-auto">
            We're making major improvements to Quoteplicity! Current subscribers aren't affected, 
            but new sign-ups will join our waitlist for the upgraded platform.
          </p>
        </motion.div>

        {/* Benefits */}
        {currentStep === 1 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
          >
            <div className="flex items-center gap-3 bg-[#e6faf7] p-4 rounded-xl">
              <Rocket className="w-8 h-8 text-[#10c0a5]" />
              <div>
                <p className="font-medium text-slate-900">First Access</p>
                <p className="text-sm text-slate-600">Launch in order of signup</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-[#e6faf7] p-4 rounded-xl">
              <Sparkles className="w-8 h-8 text-[#10c0a5]" />
              <div>
                <p className="font-medium text-slate-900">Better Features</p>
                <p className="text-sm text-slate-600">Enhanced functionality</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-[#e6faf7] p-4 rounded-xl">
              <CheckCircle2 className="w-8 h-8 text-[#10c0a5]" />
              <div>
                <p className="font-medium text-slate-900">New Platform</p>
                <p className="text-sm text-slate-600">Easier installation and setup</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-slate-500 mb-2">
            <span>Step {currentStep} of {totalSteps}</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2 bg-slate-100" />
        </div>

        {/* Main Card */}
        <motion.div
          className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <AnimatePresence mode="wait">
            {/* Step 1: Plan Selection */}
            {currentStep === 1 && !submissionSuccess && (
              <motion.div
                key="step1"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4 }}
                className="p-4 sm:p-8 md:p-12"
                data-testid="waitlist-step-1"
              >
                <div className="text-center mb-8">
                  <span className="text-sm font-medium text-[#10c0a5] uppercase tracking-wider">Step 1</span>
                  <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mt-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Which plan interests you?
                  </h2>
                  <p className="text-slate-600">Select the plan that best fits your needs</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  {planOptions.map((plan) => (
                    <div
                      key={plan.id}
                      onClick={() => setSelectedPlan(plan.id)}
                      className={`
                        relative cursor-pointer rounded-xl border-2 p-5 transition-all duration-200
                        ${selectedPlan === plan.id 
                          ? 'border-[#10c0a5] bg-[#e6faf7]/50 ring-2 ring-[#10c0a5] ring-offset-2' 
                          : plan.id === 'professional'
                            ? 'border-[#10c0a5] hover:shadow-md'
                            : 'border-slate-200 hover:border-[#10c0a5]/50 hover:shadow-md'
                        }
                      `}
                      data-testid={`plan-${plan.id}`}
                    >
                      {plan.id === 'professional' && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#10c0a5] text-white text-xs font-semibold px-3 py-1 rounded-full">
                          Most Popular
                        </span>
                      )}
                      {selectedPlan === plan.id && (
                        <CheckCircle2 className="absolute top-3 right-3 w-5 h-5 text-[#10c0a5]" />
                      )}
                      <h3 className="text-lg font-semibold text-slate-900 mb-1">{plan.name}</h3>
                      <p className="text-xl font-bold text-[#10c0a5] mb-2">{plan.price}</p>
                      <p className="text-sm text-slate-600 leading-snug">{plan.description}</p>
                    </div>
                  ))}
                </div>

                <div className="flex justify-center sm:justify-end">
                  <button
                    onClick={() => setCurrentStep(2)}
                    disabled={!canProceedStep1}
                    className={`
                      flex items-center justify-center gap-2 w-full sm:w-auto px-6 sm:px-8 py-3 rounded-lg font-medium transition-all duration-200
                      ${canProceedStep1 
                        ? 'bg-[#10c0a5] hover:bg-[#0ea892] text-white shadow-md hover:shadow-lg' 
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      }
                    `}
                    data-testid="continue-step1"
                  >
                    <span className="hidden sm:inline">Continue to Business Info</span>
                    <span className="sm:hidden">Continue</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Business Questions */}
            {currentStep === 2 && !submissionSuccess && (
              <motion.div
                key="step2"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4 }}
                className="p-4 sm:p-8 md:p-12"
                data-testid="waitlist-step-2"
              >
                <div className="text-center mb-8">
                  <span className="text-sm font-medium text-[#10c0a5] uppercase tracking-wider">Step 2</span>
                  <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mt-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Tell Us About Your Business
                  </h2>
                  <p className="text-slate-600">Help us understand your current setup</p>
                </div>

                <div className="space-y-6">
                  {/* Has Website */}
                  <div className="space-y-3">
                    <Label className="text-slate-700 font-medium flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Do you currently have a website?
                    </Label>
                    <RadioGroup value={hasWebsite?.toString()} onValueChange={(val) => setHasWebsite(val === 'true')}>
                      <div className="flex gap-4">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="true" id="website-yes" />
                          <Label htmlFor="website-yes">Yes</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="false" id="website-no" />
                          <Label htmlFor="website-no">No</Label>
                        </div>
                      </div>
                    </RadioGroup>
                    {hasWebsite && (
                      <div>
                        <Input
                          type="url"
                          placeholder="https://www.yourwebsite.com"
                          value={websiteUrl}
                          onChange={(e) => setWebsiteUrl(e.target.value)}
                          className={`h-12 mt-2 ${websiteUrl && !isValidUrl(websiteUrl) ? 'border-red-500' : ''}`}
                        />
                        {websiteUrl && !isValidUrl(websiteUrl) && (
                          <p className="text-red-500 text-sm mt-1">Please enter a valid website URL</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Uses CRM */}
                  <div className="space-y-3">
                    <Label className="text-slate-700 font-medium">
                      Are you currently using a CRM system?
                    </Label>
                    <RadioGroup value={usesCRM?.toString()} onValueChange={(val) => setUsesCRM(val === 'true')}>
                      <div className="flex gap-4">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="true" id="crm-yes" />
                          <Label htmlFor="crm-yes">Yes</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="false" id="crm-no" />
                          <Label htmlFor="crm-no">No</Label>
                        </div>
                      </div>
                    </RadioGroup>
                    {usesCRM && (
                      <Input
                        placeholder="Which CRM? (e.g., Go High Level, Zoho, Digital BGA, Pipedrive)"
                        value={crmName}
                        onChange={(e) => setCrmName(e.target.value)}
                        className="h-12 mt-2"
                      />
                    )}
                  </div>

                  {/* Agency Size */}
                  <div className="space-y-3">
                    <Label className="text-slate-700 font-medium flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      How many people are in your agency?
                    </Label>
                    <Select value={agencySize} onValueChange={setAgencySize}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select agency size" />
                      </SelectTrigger>
                      <SelectContent>
                        {agencySizeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* States Licensed */}
                  <div className="space-y-3">
                    <Label className="text-slate-700 font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      How many states are you licensed in?
                    </Label>
                    <Select value={statesLicensed} onValueChange={setStatesLicensed}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select number of states" />
                      </SelectTrigger>
                      <SelectContent>
                        {statesLicensedOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-6">
                  <button
                    onClick={goBack}
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    Back
                  </button>
                  <button
                    onClick={() => setCurrentStep(3)}
                    disabled={!canProceedStep2}
                    className={`
                      flex items-center justify-center gap-2 px-6 sm:px-8 py-3 rounded-lg font-medium transition-all duration-200
                      ${canProceedStep2 
                        ? 'bg-[#10c0a5] hover:bg-[#0ea892] text-white shadow-md hover:shadow-lg' 
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      }
                    `}
                  >
                    <span className="hidden sm:inline">Continue to Advertising</span>
                    <span className="sm:hidden">Continue</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Advertising & Contact */}
            {currentStep === 3 && !submissionSuccess && (
              <motion.div
                key="step3"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4 }}
                className="p-4 sm:p-8 md:p-12"
                data-testid="waitlist-step-3"
              >
                <div className="text-center mb-8">
                  <span className="text-sm font-medium text-[#10c0a5] uppercase tracking-wider">Step 3</span>
                  <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mt-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Advertising & Marketing
                  </h2>
                  <p className="text-slate-600">Tell us about your marketing efforts</p>
                </div>

                <div className="space-y-6">
                  {/* Runs Paid Ads */}
                  <div className="space-y-3">
                    <Label className="text-slate-700 font-medium flex items-center gap-2">
                      <Megaphone className="w-4 h-4" />
                      Are you currently running any paid ads?
                    </Label>
                    <RadioGroup value={runsPaidAds?.toString()} onValueChange={(val) => setRunsPaidAds(val === 'true')}>
                      <div className="flex gap-4">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="true" id="ads-yes" />
                          <Label htmlFor="ads-yes">Yes</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="false" id="ads-no" />
                          <Label htmlFor="ads-no">No</Label>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Ad Platforms */}
                  {runsPaidAds && (
                    <div className="space-y-3">
                      <Label className="text-slate-700 font-medium">
                        Which platforms are you running ads on?
                      </Label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {adPlatforms.map((platform) => (
                          <div
                            key={platform.id}
                            onClick={() => handlePlatformToggle(platform.id)}
                            className={`
                              flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all
                              ${adPlatformsSelected.includes(platform.id)
                                ? 'border-[#10c0a5] bg-[#e6faf7]/50'
                                : 'border-slate-200 hover:border-slate-300'
                              }
                            `}
                          >
                            <Checkbox 
                              checked={adPlatformsSelected.includes(platform.id)}
                              className="pointer-events-none"
                            />
                            <span className="text-sm font-medium">{platform.label}</span>
                          </div>
                        ))}
                      </div>
                      
                      {/* Other platform text input */}
                      {adPlatformsSelected.includes('other') && (
                        <div className="mt-3">
                          <Input
                            placeholder="Please specify other platform(s)"
                            value={otherPlatform}
                            onChange={(e) => setOtherPlatform(e.target.value)}
                            className="h-12"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-6">
                  <button
                    onClick={goBack}
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    Back
                  </button>
                  <button
                    onClick={() => setCurrentStep(4)}
                    disabled={runsPaidAds === null}
                    className={`
                      flex items-center justify-center gap-2 px-6 sm:px-8 py-3 rounded-lg font-medium transition-all duration-200
                      ${runsPaidAds !== null
                        ? 'bg-[#10c0a5] hover:bg-[#0ea892] text-white shadow-md hover:shadow-lg' 
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      }
                    `}
                  >
                    <span className="hidden sm:inline">Continue to Contact Info</span>
                    <span className="sm:hidden">Continue</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Contact Info & Submit */}
            {currentStep === 4 && !submissionSuccess && (
              <motion.div
                key="step4"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4 }}
                className="p-4 sm:p-8 md:p-12"
                data-testid="waitlist-step-4"
              >
                <div className="text-center mb-8">
                  <span className="text-sm font-medium text-[#10c0a5] uppercase tracking-wider">Step 4</span>
                  <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mt-2 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Your Contact Information
                  </h2>
                  <p className="text-slate-600">We'll notify you when it's your turn!</p>
                </div>

                <form onSubmit={contactForm.handleSubmit(handleContactSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-700 font-medium">Full Name *</Label>
                      <Input
                        id="name"
                        placeholder="John Smith"
                        className="h-12"
                        {...contactForm.register('name')}
                      />
                      {contactForm.formState.errors.name && (
                        <p className="text-red-500 text-sm">{contactForm.formState.errors.name.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company_name" className="text-slate-700 font-medium">Company Name *</Label>
                      <Input
                        id="company_name"
                        placeholder="Your Agency Name"
                        className="h-12"
                        {...contactForm.register('company_name')}
                      />
                      {contactForm.formState.errors.company_name && (
                        <p className="text-red-500 text-sm">{contactForm.formState.errors.company_name.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-700 font-medium">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@example.com"
                        className="h-12"
                        {...contactForm.register('email')}
                      />
                      {contactForm.formState.errors.email && (
                        <p className="text-red-500 text-sm">{contactForm.formState.errors.email.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-700 font-medium">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        inputMode="numeric"
                        placeholder="(555) 123-4567"
                        className="h-12"
                        {...contactForm.register('phone')}
                        onChange={(e) => {
                          const formatted = formatPhoneNumber(e.target.value);
                          contactForm.setValue('phone', formatted);
                        }}
                      />
                      {contactForm.formState.errors.phone && (
                        <p className="text-red-500 text-sm">{contactForm.formState.errors.phone.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-4">
                    <button
                      type="button"
                      onClick={goBack}
                      disabled={isSubmitting}
                      className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50"
                    >
                      <ChevronLeft className="w-5 h-5" />
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex items-center justify-center gap-2 px-6 sm:px-8 py-3 rounded-lg font-medium bg-[#10c0a5] hover:bg-[#0ea892] text-white shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <span className="hidden sm:inline">Submit Application</span>
                          <span className="sm:hidden">Submit</span>
                          <ChevronRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Success State */}
            {submissionSuccess && (
              <motion.div
                key="success"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                className="p-4 sm:p-8 md:p-12 text-center"
                data-testid="waitlist-success"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                  className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#e6faf7] mb-6"
                >
                  <CheckCircle2 className="w-10 h-10 text-[#10c0a5]" />
                </motion.div>

                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  You're on the Waitlist!
                </h2>
                <p className="text-slate-600 text-lg mb-6 max-w-md mx-auto">
                  Thank you for your interest in Quoteplicity! We'll contact you as soon as 
                  a spot opens up on our new platform.
                </p>

                <div className="bg-slate-50 rounded-xl p-6 max-w-md mx-auto text-left">
                  <h3 className="font-semibold text-slate-900 mb-3">What happens next?</h3>
                  <ul className="space-y-2 text-slate-600 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                      <span>You'll receive a confirmation email shortly</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                      <span>We'll notify you when your spot is ready</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                      <span>New clients launch in order of signup</span>
                    </li>
                  </ul>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-8 text-slate-500 text-sm"
        >
          <p>
            Questions? Contact us at{' '}
            <a href="mailto:support@quoteplicity.com" className="text-[#10c0a5] hover:underline font-medium">
              support@quoteplicity.com
            </a>
          </p>
        </motion.div>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default WaitlistPage;
